import React, { useState } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  TableSortLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import styles from "./FileManager.module.css";
import FileDirRow from "./FileDirRow";
import { handleFileDownload } from "libs/utils/file";
import { getDocumentPreviewUrl } from "api/canshare/canshareApi";
import { ProgressCircular } from "components";
/**
 * @component FileManager
 * @param { Object } props
 * @param { object } props.activeDoc
 * @param { object } props.setActiveDoc
 * @param { object } props.FileMgrProps
 * @param { boolean } props.tileMode
 * @param { string } props.searchTerm
 * @param { object } props.handleDelete
 * @returns
 */

const FileManager = ({
  activeDoc,
  setActiveDoc,
  FileMgrProps,
  tileMode = true,
  searchTerm = "",
  handleDelete,
}) => {
  const {
    fileList,
    setFileList,
    checked,
    pending,
    changeCurrentDirectory,
    handleCheckedToggle,
    addFileToCopyMoveClipboard,
    clearCopyMoveClipboard,
    fetchLatestFileList,
    isAdmin,
    isUser,
    toggleCheckedOnAllItems,
    isRenamingFileDir,
    setIsRenamingFileDir,
  } = FileMgrProps;
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const onChangeActiveDoc = (newActiveDoc) => {
    const getPreviewUrl = async (id) => {
      if (id) {
        const doc = await getDocumentPreviewUrl(id);
        return doc;
      } else {
        return { uri: "", unsecure: false };
      }
    };

    if (newActiveDoc) {
      const file = fileList.find(
        (file) => file.vv_file_id === newActiveDoc.vv_file_id
      );
      if (file?.uri) {
        setActiveDoc(file);
      } else {
        getPreviewUrl(newActiveDoc?.vv_file_id).then((doc) => {
          file.uri = doc.uri;
          file.unsecure = doc.unsecure || false;
          const updatedFileList = fileList.map((fileObj) => {
            // if file has the target vv_file_id and is not a directory
            if (
              fileObj.vv_file_id === newActiveDoc?.vv_file_id &&
              fileObj.isDir !== 1
            ) {
              return file;
            } else {
              return fileObj;
            }
          });
          setFileList(updatedFileList);

          // Check if file can be previewed, if yes then load preview else download file
          if (file.unsecure) {
            setActiveDoc(file);
          } else {
            let fileType = file.type.split("/")[0];
            switch (fileType) {
              case "image":
                setActiveDoc(file);
                break;
              case "video":
                setActiveDoc(file);
                break;
              case "application": // pdf has 'application' as the first part of its MIME type
                let subtype = file.type.split("/")[1]; // gets the subtype of the MIME type
                if (subtype === "pdf") {
                  setActiveDoc(file);
                  break;
                } else {
                  downloadFile(file?.vv_file_id || undefined);
                }
                break;
              default:
                downloadFile(file?.vv_file_id || undefined);
                break;
            }
          }
        });
      }
    }
  };

  // old

  const directories = fileList?.filter((file) => file.isDir === 1);
  const files = fileList?.filter((file) => file.isDir === 2);

  const onClickFile = (file) => {
    onChangeActiveDoc(file);
  };

  const changeDirectory = (dir) => {
    setActiveDoc(undefined);
    changeCurrentDirectory(dir);
  };

  // old end

  const downloadFile = async (fileId) => {
    if (fileId) {
      const url = `/api/fileuploadservice/Download?FileId=${fileId}`;
      handleFileDownload(url);
    }
  };

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const dateSort = (a, b, orderBy) =>
    !!a[orderBy] &&
    !!a.tableData?.childRows &&
    !!b[orderBy] &&
    !!b.tableData?.childRows
      ? new Date(b[orderBy]) - new Date(a[orderBy])
      : 1;

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    if (orderBy === "date_created") {
      return order === "desc"
        ? (a, b) => -dateSort(a, b, orderBy)
        : (a, b) => dateSort(a, b, orderBy);
    }
    if (orderBy === "size") {
      return order === "desc"
        ? (a, b) => -descendingComparator(parseInt(a), parseInt(b), orderBy)
        : (a, b) => descendingComparator(parseInt(a), parseInt(b), orderBy);
    }
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handleSortRequest = (property) => {
    if (orderBy === property) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(property);
      setOrder("asc");
    }
  };

  return (
    <>
      {pending && <ProgressCircular />}
      {!pending && !tileMode && (
        <Grid item className={styles.full_size} xs={12}>
          <TableContainer component={Paper} className={styles.table_container}>
            <Table
              className={styles.table}
              aria-label="file table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      backgroundColor: theme.palette.background.paper,
                    }}
                    align="left"
                  >
                    <Tooltip title="Select All">
                      <Checkbox
                        edge="start"
                        checked={
                          fileList.length > 0 &&
                          checked.files.length + checked.dir.length ===
                            fileList.length
                        }
                        tabIndex={-1}
                        disableRipple
                        className={styles.check_box}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleCheckedOnAllItems();
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleCheckedOnAllItems();
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    align="left"
                    style={{
                      backgroundColor: theme.palette.background.paper,
                    }}
                    className={styles.table_cell}
                  >
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleSortRequest("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  {!isSm && (
                    <>
                      <TableCell
                        align="left"
                        style={{
                          backgroundColor: theme.palette.background.paper,
                        }}
                        className={styles.table_cell}
                      >
                        <TableSortLabel
                          active={orderBy === "size"}
                          direction={orderBy === "size" ? order : "asc"}
                          onClick={() => handleSortRequest("size")}
                        >
                          Size
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          backgroundColor: theme.palette.background.paper,
                        }}
                        className={styles.table_cell}
                      >
                        <TableSortLabel
                          active={orderBy === "type"}
                          direction={orderBy === "type" ? order : "asc"}
                          onClick={() => handleSortRequest("type")}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          backgroundColor: theme.palette.background.paper,
                        }}
                        className={styles.table_cell}
                      >
                        <TableSortLabel
                          active={orderBy === "date_created"}
                          direction={orderBy === "date_created" ? order : "asc"}
                          onClick={() => handleSortRequest("date_created")}
                        >
                          Date Created
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    align="left"
                    style={{
                      backgroundColor: theme.palette.background.paper,
                    }}
                    className={styles.table_cell}
                  ></TableCell>
                </TableRow>
              </TableHead>

              <TableBody className={styles.table_body}>
                {(() => {
                  const filteredDirs = stableSort(
                    directories,
                    getComparator(order, orderBy)
                  ).filter((dir) =>
                    dir.name.toLowerCase().includes(searchTerm.toLowerCase())
                  );
                  const filteredFiles = stableSort(
                    files,
                    getComparator(order, orderBy)
                  ).filter((file) =>
                    file.name.toLowerCase().includes(searchTerm.toLowerCase())
                  );

                  if (filteredDirs.length === 0 && filteredFiles.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No items to display. This folder may be empty, or you may need additional permissions to view its contents.
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <>
                      {filteredDirs.map((dir, index) => (
                        <FileDirRow
                          key={`dir-${index}`}
                          FileDirObj={dir}
                          changeDirectory={changeDirectory}
                          handleToggle={handleCheckedToggle}
                          checked={checked}
                          onClickFile={onClickFile}
                          activeDoc={activeDoc}
                          isDir={true}
                          addFileToCopyMoveClipboard={
                            addFileToCopyMoveClipboard
                          }
                          clearCopyMoveClipboard={clearCopyMoveClipboard}
                          handleDelete={handleDelete}
                          isSm={isSm}
                          isAdmin={isAdmin}
                          isUser={isUser}
                          isRenamingFileDir={isRenamingFileDir}
                          setIsRenamingFileDir={setIsRenamingFileDir}
                        />
                      ))}
                      {filteredFiles.map((file, index) => (
                        <FileDirRow
                          key={`file-${index}`}
                          FileDirObj={file}
                          changeDirectory={changeDirectory}
                          handleToggle={handleCheckedToggle}
                          checked={checked}
                          onClickFile={onClickFile}
                          activeDoc={activeDoc}
                          isDir={false}
                          addFileToCopyMoveClipboard={
                            addFileToCopyMoveClipboard
                          }
                          clearCopyMoveClipboard={clearCopyMoveClipboard}
                          handleDelete={handleDelete}
                          isSm={isSm}
                          isAdmin={isAdmin}
                          isUser={isUser}
                          isRenamingFileDir={isRenamingFileDir}
                          setIsRenamingFileDir={setIsRenamingFileDir}
                        />
                      ))}
                    </>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
      {!pending && tileMode && (
        <Grid container spacing={1} className={styles.tile_grid}>
          {directories
            .filter((dir) =>
              dir.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map((dir, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <FileDirRow
                  FileDirObj={dir}
                  changeDirectory={changeDirectory}
                  handleToggle={handleCheckedToggle}
                  checked={checked}
                  onClickFile={onClickFile}
                  activeDoc={activeDoc}
                  isDir={true}
                  addFileToCopyMoveClipboard={addFileToCopyMoveClipboard}
                  clearCopyMoveClipboard={clearCopyMoveClipboard}
                  handleDelete={handleDelete}
                  isSm={isSm}
                  isAdmin={isAdmin}
                  isUser={isUser}
                  tileMode={true}
                  isRenamingFileDir={isRenamingFileDir}
                  setIsRenamingFileDir={setIsRenamingFileDir}
                />
              </Grid>
            ))}
          {files
            .filter((file) =>
              file.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map((file, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <FileDirRow
                  FileDirObj={file}
                  changeDirectory={changeDirectory}
                  handleToggle={handleCheckedToggle}
                  checked={checked}
                  onClickFile={onClickFile}
                  activeDoc={activeDoc}
                  isDir={false}
                  addFileToCopyMoveClipboard={addFileToCopyMoveClipboard}
                  clearCopyMoveClipboard={clearCopyMoveClipboard}
                  handleDelete={handleDelete}
                  isSm={isSm}
                  isAdmin={isAdmin}
                  isUser={isUser}
                  tileMode={true}
                  isRenamingFileDir={isRenamingFileDir}
                  setIsRenamingFileDir={setIsRenamingFileDir}
                />
              </Grid>
            ))}
        </Grid>
      )}
    </>
  );
};

export default FileManager;
